<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

const BRIDGE_SOURCE = 'chatwoot-whatsapp-calls';
const READY_EVENT_TYPE = 'CW_CALL_BRIDGE_READY';
const RESPONSE_EVENT_TYPE = 'CW_CALL_BRIDGE_RESPONSE';
const STORAGE_KEY = 'cw-call-center-instances-v1';
const REQUEST_TIMEOUT_MS = 45000;
const CALL_CENTER_API_BASE = '/call-center-api';
const route = useRoute();
const { isAdmin } = useAdmin();
const accountId = computed(() => Number(route.params.accountId));

const TEXT = {
  title: 'Chamadas IA',
  subtitle: 'Configure sessões e automações; opere chamadas e filas em um só lugar.',
  addInstance: 'Nova instância',
  createInstance: 'Criar instância',
  createInstanceTitle: 'Nova instância',
  createInstanceBody:
    'Crie uma sessão WhatsApp Web persistente para chamadas, filas e automações.',
  instanceName: 'Nome da instância',
  instancePlaceholder: 'Ex: Comercial 01',
  channel: 'Canal',
  channelBaileys: 'WhatsApp Web / Baileys',
  cancel: 'Cancelar',
  emptyTitle: 'Nenhuma instância cadastrada',
  emptyBody:
    'Crie uma instância para conectar o WhatsApp Web e começar a organizar filas de chamadas.',
  channelsStyle: 'WhatsApp Web / Baileys',
  backendConnected: 'Backend conectado',
  backendDisconnected: 'Backend indisponível',
  extensionFallbackConnected: 'Extensão fallback conectada',
  connected: 'Conectada',
  disconnected: 'Desconectada',
  qr: 'Aguardando QR Code',
  loading: 'Carregando',
  unknown: 'Sem status',
  open: 'Abrir',
  back: 'Instâncias',
  dashboard: 'Instâncias',
  instance: 'Instância',
  generateQr: 'Obter QR Code',
  pairingCode: 'Obter código de pareamento',
  pairingUnavailable:
    'Código de pareamento ainda não está disponível nesta central.',
  refreshStatus: 'Atualizar status',
  restart: 'Reiniciar',
  disconnect: 'Desconectar',
  remove: 'Remover',
  qrTitle: 'Escaneie o QR Code com seu WhatsApp Web',
  qrBody:
    'Para conectar, gere o QR Code e leia pelo celular. A sessão fica persistente no backend.',
  qrEmpty: 'QR Code ainda não gerado',
  qrHint: 'Quando a sessão cair, gere um novo QR Code e escaneie novamente.',
  allInstances: 'Todas as instâncias',
  connectedInstances: 'Conectadas',
  queuedCalls: 'Em fila',
  currentCall: 'Chamada atual',
  connection: 'Conexão',
  operation: 'Operação',
  lastUpdate: 'Última atualização',
  quickActions: 'Ações',
  modeSummary: 'Modo',
  finishedCalls: 'Finalizadas',
  failedCalls: 'Falhas',
  queued: 'Aguardando',
  active: 'Ativa',
  queueTitle: 'Fila de chamadas',
  queueInput: 'Cole números para ligar, um por linha',
  addNumbers: 'Adicionar números',
  startQueue: 'Iniciar fila',
  callStatus: 'Status',
  hangup: 'Encerrar',
  finishNext: 'Finalizar e próxima',
  failNext: 'Falhou e próxima',
  clearFinished: 'Limpar finalizadas',
  emptyQueue: 'Nenhum número na fila.',
  automations: 'Automações',
  automationMode: 'Modo',
  manualMode: 'Manual',
  audioMode: 'Áudio',
  audioFile: 'Áudio pré-pronto',
  chooseAudio: 'Selecionar áudio',
  playOnAnswer: 'Tocar quando atender',
  playAudioNow: 'Tocar agora',
  liveMic: 'Microfone do operador',
  micStart: 'Abrir microfone',
  micStop: 'Fechar microfone',
  recordingStart: 'Gravar chamada',
  recordingStop: 'Parar gravação',
  aiMode: 'Atendimento com IA',
  aiProvider: 'Provedor de conversação',
  aiModel: 'Modelo',
  aiApiKey: 'API key do provedor',
  aiTtsModel: 'Modelo de voz (ElevenLabs)',
  aiGreeting: 'Saudação inicial',
  aiPrompt: 'Instruções da IA',
  aiTestText: 'Texto para teste',
  aiTestReply: 'Testar IA',
  aiSpeak: 'Falar texto',
  aiSoon: 'Conversação por um dos cinco provedores suportados e voz via ElevenLabs.',
  events: 'Eventos',
  emptyEvents: 'Sem eventos ainda.',
  addCurrent: 'Adicionar à fila',
};

const instances = ref([]);
const selectedInstanceId = ref('');
const showCreatePanel = ref(false);
const newInstanceName = ref('');
const bulkNumbers = ref('');
const backendConnected = ref(false);
const bridgeConnected = ref(false);
const autoConnectInstanceId = ref('');
const micRecorder = ref(null);
const micStream = ref(null);
const micActive = ref(false);
const remoteRecordingActive = ref(false);
const aiTesting = ref(false);
const aiTestText = ref('Olá, pode confirmar se está me ouvindo bem?');
const aiProviders = ref([]);
const aiModels = ref([]);
const aiModelsLoading = ref(false);
const aiModelsError = ref('');
const aiModelsFetchedAt = ref(null);
const aiApiKey = ref('');
const pendingBridgeRequests = new Map();
let aiModelsGeneration = 0;

const selectedInstance = computed(() =>
  instances.value.find(instance => instance.id === selectedInstanceId.value)
);
const selectedAiProvider = computed(() =>
  aiProviders.value.find(item => item.id === selectedInstance.value?.config?.ai?.provider)
);

const sortedInstances = computed(() =>
  [...instances.value].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
);

const hasActiveCall = computed(() =>
  selectedInstance.value?.queue?.some(item => item.status === 'calling')
);

const connectionOk = computed(
  () => backendConnected.value || bridgeConnected.value
);

const connectionLabel = computed(() => {
  if (backendConnected.value) return TEXT.backendConnected;
  if (bridgeConnected.value) return TEXT.extensionFallbackConnected;
  return TEXT.backendDisconnected;
});

const normalizePhone = rawValue => {
  let digits = String(rawValue || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
  if (digits.length < 11 || digits.length > 13) return '';
  return digits;
};

const formatPhone = rawValue => {
  const digits = normalizePhone(rawValue);
  if (!digits) return '';
  if (digits.startsWith('55') && digits.length >= 12) {
    return `+55 ${digits.slice(2, 4)} ${digits.slice(4)}`;
  }
  return `+${digits}`;
};

const extractPhones = text => {
  const matches =
    String(text || '').match(
      /(?:\+?55\s*)?(?:\(?\d{2}\)?\s*)?(?:9\s*)?\d{4,5}[-.\s]?\d{4}/g
    ) || [];
  return [...new Set(matches.map(normalizePhone).filter(Boolean))];
};

const makeId = prefix =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const defaultAutomationConfig = () => ({
  mode: 'manual',
  audioFileId: '',
  audioFileName: '',
  playAudioOnAnswer: false,
  liveMicEnabled: false,
  ai: {
    enabled: false,
    provider: 'openrouter',
    model: 'google/gemini-2.5-flash-lite',
    sttModel: 'openai/gpt-4o-mini-transcribe',
    ttsProvider: 'elevenlabs',
    ttsModel: 'eleven_v3',
    voiceId: '',
    greetingText:
      'Boa tarde. Sou a assistente virtual da empresa. Como posso ajudar?',
    instructions: '',
  },
});

const defaultCallState = () => ({
  status: 'idle',
  detail: '',
  playbackStatus: '',
  recordingFileName: '',
});

const defaultInstance = name => ({
  id: makeId('call-instance'),
  name,
  status: 'disconnected',
  phone: '',
  qrDataUrl: '',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  queue: [],
  logs: [],
  audioUploads: [],
  call: defaultCallState(),
  config: defaultAutomationConfig(),
});

const normalizeInstanceShape = instance => {
  const config = {
    ...defaultAutomationConfig(),
    ...(instance.config || {}),
    ai: {
      ...defaultAutomationConfig().ai,
      ...(instance.config?.ai || {}),
      enabled: !!(instance.config?.ai?.enabled || instance.config?.aiEnabled),
    },
  };

  if (config.ai.provider === 'openrouter-elevenlabs') {
    config.ai.provider = 'openrouter';
  }

  if (instance.config?.aiEnabled && config.mode === 'manual') {
    config.mode = 'ai';
  }

  return {
    ...instance,
    queue: Array.isArray(instance.queue) ? instance.queue : [],
    logs: Array.isArray(instance.logs) ? instance.logs : [],
    audioUploads: Array.isArray(instance.audioUploads)
      ? instance.audioUploads
      : [],
    call: {
      ...defaultCallState(),
      ...(instance.call || {}),
    },
    config,
    queueRevision: Number(instance.queueRevision || 0),
  };
};

const loadCachedInstances = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    instances.value = Array.isArray(parsed)
      ? parsed.map(normalizeInstanceShape)
      : [];
  } catch (_error) {
    instances.value = [];
  }
};

const loadInstances = async () => {
  loadCachedInstances();
  try {
    const response = await sendBackendRequest('/api/instances');
    const cached = new Map(instances.value.map(item => [item.id, item]));
    instances.value = (response.instances || []).map(item => normalizeInstanceShape({
      ...(cached.get(item.id) || {}),
      ...item,
      queue: item.queue || [],
      queueRevision: item.queueRevision || 0,
    }));
  } catch (error) { useAlert(error.message); }
};

const saveInstances = () => {
  try {
    const cache = instances.value.map(instance => ({ ...instance, qrDataUrl: '', logs: [] }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (_error) {
    // A full or restricted local cache must not interrupt call operations.
  }
};

const touchInstance = instance => {
  if (instance) instance.updatedAt = new Date().toISOString();
};

const addLog = (instance, message) => {
  if (!instance) return;
  instance.logs = [
    {
      id: makeId('log'),
      at: new Date().toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
      message,
    },
    ...(instance.logs || []),
  ].slice(0, 30);
  touchInstance(instance);
};

const removeInstance = instance => {
  instances.value = instances.value.filter(item => item.id !== instance.id);
  if (selectedInstanceId.value === instance.id) selectedInstanceId.value = '';
};

const statusLabel = status =>
  ({
    connected: TEXT.connected,
    disconnected: TEXT.disconnected,
    qr: TEXT.qr,
    loading: TEXT.loading,
    unknown: TEXT.unknown,
  })[status] || TEXT.unknown;

const statusClass = status =>
  ({
    connected: 'bg-n-teal-3 text-n-teal-11 border-n-teal-7',
    disconnected: 'bg-n-slate-3 text-n-slate-11 border-n-weak',
    qr: 'bg-n-amber-3 text-n-amber-11 border-n-amber-7',
    loading: 'bg-n-blue-3 text-n-blue-11 border-n-blue-7',
    unknown: 'bg-n-slate-3 text-n-slate-11 border-n-weak',
  })[status] || 'bg-n-slate-3 text-n-slate-11 border-n-weak';

const queueStatusLabel = status =>
  ({
    queued: 'Na fila',
    calling: 'Chamando',
    done: 'Finalizada',
    failed: 'Falhou',
  })[status] || status;

const itemCountText = count => `${count} itens`;

const instanceQueueStats = instance => {
  const queue = Array.isArray(instance?.queue) ? instance.queue : [];
  return {
    queued: queue.filter(item => item.status === 'queued').length,
    calling: queue.filter(item => item.status === 'calling').length,
    done: queue.filter(item => item.status === 'done').length,
    failed: queue.filter(item => item.status === 'failed').length,
  };
};

const totalQueuedCalls = computed(() =>
  instances.value.reduce(
    (total, instance) => total + instanceQueueStats(instance).queued,
    0
  )
);

const instanceMetrics = instance => ({
  queued: instanceQueueStats(instance).queued,
  done: instanceQueueStats(instance).done,
  failed: instanceQueueStats(instance).failed,
});

const automationModeLabel = mode =>
  ({
    manual: TEXT.manualMode,
    audio: TEXT.audioMode,
    ai: TEXT.aiMode,
  })[mode] || TEXT.manualMode;

const automationModeIcon = mode =>
  ({
    manual: 'i-lucide-user-round',
    audio: 'i-lucide-volume-2',
    ai: 'i-lucide-bot',
  })[mode] || 'i-lucide-user-round';

const formatUpdatedAt = value => {
  if (!value) return '--';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const queueResultText = result => (result ? `- ${result}` : '');

const callStatusLabel = status =>
  ({
    idle: 'Sem chamada',
    calling: 'Chamando',
    ringing: 'Chamando',
    in_call: 'Em chamada',
    ended: 'Encerrada',
    failed: 'Falhou',
    unknown: 'Sem status',
  })[status] || 'Sem status';

const queueStatusClass = status =>
  ({
    queued: 'border-n-weak',
    calling: 'bg-n-blue-3 border-n-blue-7',
    done: 'bg-n-teal-3 border-n-teal-7',
    failed: 'bg-n-ruby-3 border-n-ruby-7',
  })[status] || 'border-n-weak';

const sendBridgeRequest = (
  type,
  payload = {},
  timeoutMs = REQUEST_TIMEOUT_MS
) =>
  new Promise((resolve, reject) => {
    if (!bridgeConnected.value) {
      reject(new Error('Extensão de chamadas desconectada.'));
      return;
    }

    const requestId = makeId('bridge-request');
    const timeout = window.setTimeout(() => {
      pendingBridgeRequests.delete(requestId);
      reject(new Error('Timeout aguardando resposta da extensão.'));
    }, timeoutMs);

    pendingBridgeRequests.set(requestId, {
      resolve: response => {
        window.clearTimeout(timeout);
        resolve(response);
      },
      reject: error => {
        window.clearTimeout(timeout);
        reject(error);
      },
    });

    window.postMessage(
      {
        source: BRIDGE_SOURCE,
        type,
        requestId,
        payload,
      },
      window.location.origin
    );
  });

const checkBackendHealth = async () => {
  try {
    const response = await fetch(`${CALL_CENTER_API_BASE}/health`, {
      credentials: 'same-origin',
    });
    backendConnected.value = response.ok;
  } catch (_error) {
    backendConnected.value = false;
  }
};

const sendBackendRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${CALL_CENTER_API_BASE}${endpoint}`, {
    method: options.method || 'GET',
    credentials: 'same-origin',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      'X-Chatwoot-Account-Id': String(accountId.value),
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: AbortSignal.timeout(120000),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    if (response.status >= 500) backendConnected.value = false;
    throw Object.assign(new Error(payload?.error || `Backend retornou HTTP ${response.status}.`), { status: response.status });
  }

  backendConnected.value = true;
  return payload;
};

const sendBackendBinary = async (
  endpoint,
  { method = 'POST', body, headers }
) => {
  const response = await fetch(`${CALL_CENTER_API_BASE}${endpoint}`, {
    method,
    credentials: 'same-origin',
    headers: { 'X-Chatwoot-Account-Id': String(accountId.value), ...(headers || {}) },
    body,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    backendConnected.value = false;
    throw new Error(
      payload?.error || `Backend retornou HTTP ${response.status}.`
    );
  }

  backendConnected.value = true;
  return payload;
};

const loadAiProviders = async () => {
  try {
    const response = await sendBackendRequest('/api/ai/providers');
    aiProviders.value = response.providers || [];
  } catch (error) {
    aiModelsError.value = error.message;
  }
};

const loadAiModels = async (instance = selectedInstance.value) => {
  if (!instance?.config?.ai?.provider) return;
  const generation = ++aiModelsGeneration;
  const context = `${instance.id}:${instance.config.ai.provider}`;
  const isCurrent = () => generation === aiModelsGeneration && context === `${selectedInstance.value?.id}:${selectedInstance.value?.config?.ai?.provider}`;
  aiModelsLoading.value = true;
  aiModelsError.value = '';
  try {
    const response = await sendBackendRequest(
      `/api/ai/providers/${instance.config.ai.provider}/models`,
      {
        method: 'POST',
        body: {
          instanceId: instance.id,
          apiKey: aiApiKey.value || undefined,
        },
      }
    );
    if (!isCurrent()) return;
    aiModels.value = response.models || [];
    aiModelsFetchedAt.value = response.fetchedAt || new Date().toISOString();
    if (!aiModels.value.some(item => item.id === instance.config.ai.model)) {
      aiModelsError.value = 'O modelo salvo não aparece no catálogo atual. Escolha outro somente se desejar alterar a configuração.';
    }
  } catch (error) {
    if (!isCurrent()) return;
    aiModels.value = [];
    aiModelsFetchedAt.value = null;
    aiModelsError.value = error.message;
  } finally {
    if (isCurrent()) aiModelsLoading.value = false;
  }
};

const changeAiProvider = () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  aiApiKey.value = '';
  instance.config.ai.hasApiKey = false;
  instance.config.ai.model = selectedAiProvider.value?.defaultModel || '';
  aiModels.value = [];
  void loadAiModels(instance);
};

const applyAutomationConfig = (instance, automation = {}) => {
  if (!instance) return;
  instance.config = {
    ...defaultAutomationConfig(),
    ...(instance.config || {}),
    ...automation,
    ai: {
      ...defaultAutomationConfig().ai,
      ...(instance.config?.ai || {}),
      ...(automation.ai || {}),
    },
  };
  touchInstance(instance);
};

const refreshInstanceMedia = async instance => {
  if (!instance) return;
  try {
    const [automationResponse, audioResponse] = await Promise.all([
      sendBackendRequest(`/api/instances/${instance.id}/automation`),
      sendBackendRequest(`/api/instances/${instance.id}/audio`),
    ]);
    applyAutomationConfig(instance, automationResponse.automation);
    instance.audioUploads = audioResponse.audio || [];
    await loadAiModels(instance);
  } catch (error) {
    addLog(instance, `Mídia/automação indisponível: ${error.message}`);
  }
};

const selectInstance = instance => {
  selectedInstanceId.value = instance.id;
  refreshInstanceMedia(instance);
};

const saveAutomationConfig = async instance => {
  if (!instance) return;
  try {
    const response = await sendBackendRequest(
      `/api/instances/${instance.id}/automation`,
      {
        method: 'PUT',
        body: {
          ...instance.config,
          ai: {
            ...instance.config.ai,
            apiKey: aiApiKey.value || undefined,
          },
        },
      }
    );
    applyAutomationConfig(instance, response.automation);
    aiApiKey.value = '';
    addLog(instance, 'Automação salva no backend.');
    return response.automation;
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
    throw error;
  }
};

const sendCallCenterCommand = async ({
  instance,
  endpoint,
  method = 'POST',
  body = {},
  bridgeType,
  bridgePayload,
  timeoutMs,
}) => {
  let backendError = null;

  try {
    return await sendBackendRequest(endpoint, { method, body });
  } catch (error) {
    backendError = error;
  }

  if (bridgeConnected.value && bridgeType && !backendError.status && backendError.name !== 'TimeoutError' && backendError.name !== 'AbortError') {
    addLog(instance, 'Backend indisponível; usando extensão como fallback.');
    return sendBridgeRequest(
      bridgeType,
      bridgePayload || body,
      timeoutMs || REQUEST_TIMEOUT_MS
    );
  }

  throw backendError;
};

const applyBridgeStatus = (instance, payload = {}) => {
  instance.status = payload.status || 'unknown';
  instance.phone = payload.phone || instance.phone || '';
  instance.qrDataUrl = payload.qrDataUrl || '';
  touchInstance(instance);
};

async function connectInstance(instance) {
  try {
    instance.status = 'loading';
    addLog(instance, 'Solicitando conexão do WhatsApp Web no backend.');
    const response = await sendCallCenterCommand({
      instance,
      endpoint: `/api/instances/${instance.id}/connect`,
      body: { name: instance.name },
      bridgeType: 'CW_CALL_CONNECT_INSTANCE',
      bridgePayload: {
        instanceId: instance.id,
        name: instance.name,
      },
    });
    if (!response?.ok) throw new Error(response?.error || 'Falha ao conectar.');
    applyBridgeStatus(instance, response);
    addLog(instance, `Status atualizado: ${statusLabel(instance.status)}.`);
  } catch (error) {
    instance.status = 'disconnected';
    addLog(instance, error.message);
    useAlert(error.message);
  }
}

function requestQrForInstance(instance) {
  if (!instance) return;
  instance.status = 'loading';
  autoConnectInstanceId.value = instance.id;
  addLog(instance, 'Gerando QR Code automaticamente.');
  connectInstance(instance).finally(() => {
    if (autoConnectInstanceId.value === instance.id) {
      autoConnectInstanceId.value = '';
    }
  });
}

const createInstance = async () => {
  const name = newInstanceName.value.trim();
  if (!name) {
    useAlert('Informe o nome da instância.');
    return;
  }

  const instance = defaultInstance(name);
  try {
    await sendBackendRequest('/api/instances', {
      method: 'POST',
      body: {
        id: instance.id,
        name: instance.name,
      },
    });
    instances.value = [instance, ...instances.value];
    selectedInstanceId.value = instance.id;
    newInstanceName.value = '';
    showCreatePanel.value = false;
    addLog(instance, 'Instância registrada no backend.');
    await saveAutomationConfig(instance);
    await requestQrForInstance(instance);
  } catch (error) {
    useAlert(error.message);
  }
};

const saveQueue = async instance => {
  const response = await sendBackendRequest(`/api/instances/${instance.id}/queue`, {
    method: 'PUT', body: { revision: instance.queueRevision || 0, items: instance.queue },
  });
  instance.queue = response.items || [];
  instance.queueRevision = response.revision;
};

const refreshInstanceStatus = async instance => {
  try {
    const response = await sendCallCenterCommand({
      instance,
      endpoint: `/api/instances/${instance.id}/status?name=${encodeURIComponent(
        instance.name
      )}`,
      method: 'GET',
      bridgeType: 'CW_CALL_GET_INSTANCE_STATUS',
      bridgePayload: {
        instanceId: instance.id,
      },
    });
    if (!response?.ok)
      throw new Error(response?.error || 'Falha ao atualizar.');
    applyBridgeStatus(instance, response);
    addLog(instance, `Status atualizado: ${statusLabel(instance.status)}.`);
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const restartInstance = async instance => {
  try {
    instance.status = 'loading';
    addLog(instance, 'Reiniciando sessão WhatsApp Web no backend.');
    const response = await sendCallCenterCommand({
      instance,
      endpoint: `/api/instances/${instance.id}/restart`,
      body: { name: instance.name },
      bridgeType: 'CW_CALL_RESTART_INSTANCE',
      bridgePayload: {
        instanceId: instance.id,
      },
    });
    if (!response?.ok)
      throw new Error(response?.error || 'Falha ao reiniciar.');
    applyBridgeStatus(instance, response);
    addLog(instance, `Reiniciada: ${statusLabel(instance.status)}.`);
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const disconnectInstance = async instance => {
  try {
    const response = await sendCallCenterCommand({
      instance,
      endpoint: `/api/instances/${instance.id}/disconnect`,
      body: { name: instance.name },
      bridgeType: 'CW_CALL_DISCONNECT_INSTANCE',
      bridgePayload: {
        instanceId: instance.id,
      },
    });
    if (!response?.ok)
      throw new Error(response?.error || 'Falha ao desconectar.');
    instance.status = 'disconnected';
    instance.qrDataUrl = '';
    addLog(instance, 'Sessão desconectada.');
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const addNumbersToQueue = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;

  const phones = extractPhones(bulkNumbers.value);
  let added = 0;
  phones.forEach(phone => {
    const exists = instance.queue.some(
      item =>
        item.phone === phone && ['queued', 'calling'].includes(item.status)
    );
    if (!exists) {
      instance.queue.push({
        id: makeId('call-queue'),
        phone,
        status: 'queued',
        result: '',
      });
      added += 1;
    }
  });

  bulkNumbers.value = '';
  addLog(instance, `${added} número(s) adicionados à fila.`);
  try { await saveQueue(instance); } catch (error) { await loadInstances(); useAlert(`A fila mudou em outra tela. Atualizamos os dados: ${error.message}`); }
};

const startNextCall = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;

  if (hasActiveCall.value) {
    useAlert('Finalize a chamada ativa antes de iniciar a próxima.');
    return;
  }

  const item = instance.queue.find(entry => entry.status === 'queued');
  if (!item) {
    addLog(instance, 'Fila concluída.');
    return;
  }

  item.status = 'calling';
  item.result = 'Solicitando chamada';
  try { await saveQueue(instance); } catch (error) { await loadInstances(); useAlert('A fila mudou em outra tela. Tente novamente.'); return; }
  addLog(instance, `Iniciando chamada para ${formatPhone(item.phone)}.`);

  try {
    const response = await sendCallCenterCommand({
      instance,
      endpoint: `/api/instances/${instance.id}/calls/start`,
      body: {
        name: instance.name,
        phone: item.phone,
        mode: instance.config.mode,
        audioFileId: instance.config.audioFileId,
        playAudioOnAnswer: instance.config.playAudioOnAnswer,
      },
      bridgeType: 'CW_CALL_START',
      bridgePayload: {
        instanceId: instance.id,
        phone: item.phone,
      },
    });
    if (!response?.ok) throw new Error(response?.error || 'Falha ao iniciar.');
    instance.call = {
      ...defaultCallState(),
      ...(response.activeCall || {}),
      status: response.status || response.activeCall?.status || 'calling',
    };
    item.result = backendConnected.value
      ? 'Chamada iniciada no backend'
      : 'Chamada iniciada no WhatsApp Web';
    addLog(instance, `WhatsApp Web acionado para ${formatPhone(item.phone)}.`);
    await saveQueue(instance).catch(error => useAlert(`Chamada iniciada, mas a fila mudou em outra tela: ${error.message}`));
  } catch (error) {
    item.status = 'failed';
    item.result = error.message;
    addLog(instance, error.message);
    await saveQueue(instance).catch(() => {});
  }
};

const finishActiveAndNext = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  if (!(await hangupActiveCall())) return;
  await startNextCall();
};

const failActiveAndNext = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  if (!(await hangupActiveCall())) return;
  const item = instance.queue.find(entry => entry.status === 'calling');
  const lastCompleted = item || [...instance.queue].reverse().find(entry => entry.status === 'done');
  if (lastCompleted) {
    lastCompleted.status = 'failed';
    lastCompleted.result = 'Marcada como falha';
  }
  await saveQueue(instance);
  await startNextCall();
};

const clearFinished = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  instance.queue = instance.queue.filter(item =>
    ['queued', 'calling'].includes(item.status)
  );
  await saveQueue(instance);
};

const removeQueueItem = async id => {
  const instance = selectedInstance.value;
  if (!instance) return;
  const item = instance.queue.find(entry => entry.id === id);
  if (item?.status === 'calling') return useAlert('Encerre a chamada antes de removê-la.');
  instance.queue = instance.queue.filter(entry => entry.id !== id);
  await saveQueue(instance);
};

const setAutomationMode = mode => {
  const instance = selectedInstance.value;
  if (!instance) return;
  instance.config.mode = mode;
  if (mode === 'ai') instance.config.ai.enabled = true;
};

const onAudioFileChange = async event => {
  const instance = selectedInstance.value;
  const file = event.target.files?.[0];
  if (!instance || !file) return;
  try {
    addLog(instance, `Enviando áudio: ${file.name}.`);
    const response = await sendBackendBinary(
      `/api/instances/${instance.id}/audio?name=${encodeURIComponent(
        instance.name
      )}`,
      {
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'x-file-name': encodeURIComponent(file.name),
        },
      }
    );
    instance.audioUploads = [
      response.audio,
      ...(instance.audioUploads || []).filter(
        audio => audio.id !== response.audio.id
      ),
    ];
    applyAutomationConfig(instance, response.automation);
    addLog(instance, `Áudio pronto: ${response.audio.originalName}.`);
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  } finally {
    event.target.value = '';
  }
};

const playSelectedAudioNow = async () => {
  const instance = selectedInstance.value;
  if (!instance?.config.audioFileId) {
    useAlert('Selecione um áudio antes de tocar.');
    return;
  }
  try {
    await sendBackendRequest(`/api/instances/${instance.id}/calls/play-audio`, {
      method: 'POST',
      body: {
        name: instance.name,
        audioFileId: instance.config.audioFileId,
      },
    });
    addLog(instance, 'Áudio enviado para o microfone virtual.');
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const testAiReply = async ({ playAudio = true } = {}) => {
  const instance = selectedInstance.value;
  if (!instance) return;
  const inputText = aiTestText.value.trim();
  if (!inputText) {
    useAlert('Digite um texto para testar a IA.');
    return;
  }

  try {
    aiTesting.value = true;
    await saveAutomationConfig(instance);
    const response = await sendBackendRequest(
      `/api/instances/${instance.id}/ai/reply`,
      {
        method: 'POST',
        body: {
          name: instance.name,
          inputText,
          playAudio,
        },
      }
    );
    const reply = response.reply?.text || '';
    if (reply) addLog(instance, `IA: ${reply}`);
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  } finally {
    aiTesting.value = false;
  }
};

const speakAiText = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  const text = aiTestText.value.trim();
  if (!text) {
    useAlert('Digite um texto para a voz falar.');
    return;
  }

  try {
    aiTesting.value = true;
    await saveAutomationConfig(instance);
    await sendBackendRequest(`/api/instances/${instance.id}/ai/speak`, {
      method: 'POST',
      body: {
        name: instance.name,
        text,
      },
    });
    addLog(instance, 'Texto enviado para a voz da IA.');
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  } finally {
    aiTesting.value = false;
  }
};

const refreshCallStatus = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  try {
    const response = await sendBackendRequest(
      `/api/instances/${instance.id}/calls/status?name=${encodeURIComponent(
        instance.name
      )}`
    );
    instance.call = {
      ...defaultCallState(),
      ...(response.activeCall || {}),
      status: response.status || response.activeCall?.status || 'idle',
      detail: response.detail || response.activeCall?.detail || '',
      playbackStatus: response.mediaBridge?.playbackStatus || '',
    };
    addLog(instance, `Chamada: ${callStatusLabel(instance.call.status)}.`);
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const hangupActiveCall = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  try {
    const response = await sendBackendRequest(
      `/api/instances/${instance.id}/calls/hangup`,
      {
        method: 'POST',
        body: { name: instance.name },
      }
    );
    if (!response?.ok) throw new Error(response?.error || 'Falha ao encerrar.');
    instance.call = {
      ...defaultCallState(),
      ...(response.activeCall || {}),
      status: response.status || 'ended',
    };
    const item = instance.queue.find(entry => entry.status === 'calling');
    if (item) {
      item.status = 'done';
      item.result = 'Encerrada';
    }
    addLog(instance, 'Chamada encerrada.');
    await saveQueue(instance);
    return true;
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
    return false;
  }
};

const sendOperatorMicChunk = async (instance, blob, mimeType) => {
  try {
    await sendBackendBinary(
      `/api/instances/${instance.id}/calls/mic-chunk?name=${encodeURIComponent(
        instance.name
      )}`,
      {
        body: blob,
        headers: {
          'Content-Type': mimeType || blob.type || 'audio/webm',
        },
      }
    );
  } catch (error) {
    addLog(instance, `Microfone: ${error.message}`);
  }
};

const startOperatorMic = async () => {
  const instance = selectedInstance.value;
  if (!instance || micActive.value) return;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';
    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = event => {
      if (event.data?.size) {
        sendOperatorMicChunk(instance, event.data, mimeType);
      }
    };
    recorder.start(1000);
    micStream.value = stream;
    micRecorder.value = recorder;
    micActive.value = true;
    instance.config.liveMicEnabled = true;
    saveAutomationConfig(instance);
    addLog(instance, 'Microfone do operador aberto.');
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const stopOperatorMic = () => {
  const instance = selectedInstance.value;
  if (micRecorder.value && micRecorder.value.state !== 'inactive') {
    micRecorder.value.stop();
  }
  micStream.value?.getTracks().forEach(track => track.stop());
  micRecorder.value = null;
  micStream.value = null;
  micActive.value = false;
  if (instance) {
    instance.config.liveMicEnabled = false;
    saveAutomationConfig(instance);
    addLog(instance, 'Microfone do operador fechado.');
  }
};

const startRemoteRecording = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  try {
    await sendBackendRequest(
      `/api/instances/${instance.id}/calls/recording/start`,
      {
        method: 'POST',
        body: { name: instance.name },
      }
    );
    remoteRecordingActive.value = true;
    addLog(instance, 'Gravação iniciada.');
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const stopRemoteRecording = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;
  try {
    const response = await sendBackendRequest(
      `/api/instances/${instance.id}/calls/recording/stop`,
      {
        method: 'POST',
        body: { name: instance.name },
      }
    );
    remoteRecordingActive.value = false;
    instance.call.recordingFileName = response.recording?.fileName || '';
    addLog(instance, `Gravação salva: ${instance.call.recordingFileName}.`);
  } catch (error) {
    addLog(instance, error.message);
    useAlert(error.message);
  }
};

const onBridgeMessage = event => {
  if (event.source !== window) return;
  if (event.data?.source !== BRIDGE_SOURCE) return;

  if (event.data.type === READY_EVENT_TYPE) {
    bridgeConnected.value = !!event.data.payload?.connected;
    if (
      !backendConnected.value &&
      bridgeConnected.value &&
      autoConnectInstanceId.value
    ) {
      const instance = instances.value.find(
        item => item.id === autoConnectInstanceId.value
      );
      autoConnectInstanceId.value = '';
      if (instance) connectInstance(instance);
    }
    return;
  }

  if (event.data.type === RESPONSE_EVENT_TYPE) {
    const pending = pendingBridgeRequests.get(event.data.requestId);
    if (!pending) return;
    pendingBridgeRequests.delete(event.data.requestId);
    pending.resolve(event.data.payload);
  }
};

watch(instances, saveInstances, { deep: true });

onMounted(() => {
  void loadInstances();
  window.addEventListener('message', onBridgeMessage);
  checkBackendHealth();
  void loadAiProviders();
});

onBeforeUnmount(() => {
  ++aiModelsGeneration;
  if (micActive.value) stopOperatorMic();
  window.removeEventListener('message', onBridgeMessage);
  pendingBridgeRequests.forEach(({ reject }) =>
    reject(new Error('Central de chamadas desmontada.'))
  );
  pendingBridgeRequests.clear();
});
</script>

<template>
  <main class="flex h-full w-full flex-col overflow-hidden bg-n-slate-1">
    <header
      class="flex flex-col gap-3 border-b border-n-weak bg-n-background px-6 py-4 md:flex-row md:items-center md:justify-between"
    >
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span class="i-lucide-phone-call size-5 text-n-blue-10" />
          <h1 class="m-0 text-xl font-semibold text-n-slate-12">
            {{ TEXT.title }}
          </h1>
        </div>
        <p class="m-0 mt-1 text-sm text-n-slate-10">
          {{ TEXT.subtitle }}
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <span
          class="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium"
          :class="
            connectionOk
              ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
              : 'border-n-weak bg-n-slate-3 text-n-slate-11'
          "
        >
          <span
            class="size-2 rounded-full"
            :class="connectionOk ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          />
          {{ connectionLabel }}
        </span>
        <button
          v-if="isAdmin"
          type="button"
          class="inline-flex h-9 items-center gap-2 rounded-md bg-n-blue-9 px-3 text-sm font-medium text-white hover:bg-n-blue-10"
          @click="showCreatePanel = true"
        >
          <span class="i-lucide-plus size-4" />
          {{ TEXT.addInstance }}
        </button>
      </div>
    </header>

    <div
      v-if="showCreatePanel"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="showCreatePanel = false"
    >
      <section
        class="w-full max-w-lg rounded-lg border border-n-weak bg-n-background shadow-xl"
      >
        <div
          class="flex items-start justify-between gap-3 border-b border-n-weak p-5"
        >
          <div class="min-w-0">
            <h2 class="m-0 text-lg font-semibold text-n-slate-12">
              {{ TEXT.createInstanceTitle }}
            </h2>
            <p class="m-0 mt-1 text-sm text-n-slate-10">
              {{ TEXT.createInstanceBody }}
            </p>
          </div>
          <button
            type="button"
            class="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-slate-2"
            @click="showCreatePanel = false"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="space-y-4 p-5">
          <label class="block">
            <span class="text-xs font-medium text-n-slate-11">
              {{ TEXT.instanceName }}
            </span>
            <input
              v-model="newInstanceName"
              class="mt-1 h-10 w-full rounded-md border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
              :placeholder="TEXT.instancePlaceholder"
              @keydown.enter="createInstance"
            />
          </label>
          <label class="block">
            <span class="text-xs font-medium text-n-slate-11">
              {{ TEXT.channel }}
            </span>
            <select
              class="mt-1 h-10 w-full rounded-md border border-n-weak bg-n-slate-2 px-3 text-sm text-n-slate-12 outline-none"
              disabled
            >
              <option>{{ TEXT.channelBaileys }}</option>
            </select>
          </label>
        </div>

        <div class="flex justify-end gap-2 border-t border-n-weak p-5">
          <button
            type="button"
            class="rounded-md border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2"
            @click="showCreatePanel = false"
          >
            {{ TEXT.cancel }}
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-md bg-n-blue-9 px-3 py-2 text-sm font-medium text-white hover:bg-n-blue-10"
            @click="createInstance"
          >
            <span class="i-lucide-plus size-4" />
            {{ TEXT.createInstance }}
          </button>
        </div>
      </section>
    </div>

    <section v-if="!selectedInstance" class="flex-1 overflow-y-auto p-6">
      <div
        class="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h2 class="m-0 text-lg font-semibold text-n-slate-12">
            {{ TEXT.dashboard }}
          </h2>
          <p class="m-0 mt-1 text-sm text-n-slate-10">
            {{ itemCountText(instances.length) }}
          </p>
        </div>
      </div>

      <div class="mb-4 grid gap-3 md:grid-cols-3">
        <div class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="flex items-center justify-between gap-3">
            <div>
              <div class="text-xs font-medium text-n-slate-10">
                {{ TEXT.allInstances }}
              </div>
              <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                {{ instances.length }}
              </div>
            </div>
            <span class="i-lucide-layers-3 size-6 text-n-slate-9" />
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="flex items-center justify-between gap-3">
            <div>
              <div class="text-xs font-medium text-n-slate-10">
                {{ TEXT.connectedInstances }}
              </div>
              <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                {{
                  instances.filter(instance => instance.status === 'connected')
                    .length
                }}
              </div>
            </div>
            <span class="i-lucide-wifi size-6 text-n-teal-10" />
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="flex items-center justify-between gap-3">
            <div>
              <div class="text-xs font-medium text-n-slate-10">
                {{ TEXT.queuedCalls }}
              </div>
              <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                {{ totalQueuedCalls }}
              </div>
            </div>
            <span class="i-lucide-list-ordered size-6 text-n-blue-10" />
          </div>
        </div>
      </div>

      <div
        v-if="!instances.length"
        class="flex min-h-[360px] flex-col items-center justify-center rounded-lg border border-dashed border-n-weak bg-n-background text-center"
      >
        <span class="i-lucide-radio-tower mb-3 size-10 text-n-slate-8" />
        <h2 class="m-0 text-base font-semibold text-n-slate-12">
          {{ TEXT.emptyTitle }}
        </h2>
        <p class="mt-2 max-w-md text-sm text-n-slate-10">
          {{ TEXT.emptyBody }}
        </p>
      </div>

      <div v-else class="space-y-2">
        <button
          v-for="instance in sortedInstances"
          :key="instance.id"
          type="button"
          class="grid w-full gap-3 rounded-lg border border-n-weak bg-n-background p-4 text-left hover:border-n-blue-7 hover:bg-n-slate-2 md:grid-cols-[minmax(0,1.5fr)_160px_160px_100px]"
          @click="selectInstance(instance)"
        >
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-n-blue-3 text-n-blue-10"
            >
              <span class="i-lucide-smartphone size-5" />
            </span>
            <div class="min-w-0">
              <div class="truncate text-base font-semibold text-n-slate-12">
                {{ instance.name }}
              </div>
              <div class="mt-1 truncate text-sm text-n-slate-10">
                {{ TEXT.channelsStyle }}
              </div>
              <div class="mt-2 flex flex-wrap items-center gap-2">
                <span
                  class="rounded-md border px-2 py-1 text-xs font-medium"
                  :class="statusClass(instance.status)"
                >
                  {{ statusLabel(instance.status) }}
                </span>
                <span
                  class="inline-flex items-center gap-1 rounded-md bg-n-slate-2 px-2 py-1 text-xs text-n-slate-10"
                >
                  <span
                    class="size-3"
                    :class="automationModeIcon(instance.config.mode)"
                  />
                  {{ automationModeLabel(instance.config.mode) }}
                </span>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 md:grid-cols-1">
            <div>
              <div class="text-xs text-n-slate-10">{{ TEXT.queued }}</div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{ instanceMetrics(instance).queued }}
              </div>
            </div>
            <div>
              <div class="text-xs text-n-slate-10">
                {{ TEXT.finishedCalls }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{ instanceMetrics(instance).done }}
              </div>
            </div>
          </div>
          <div>
            <div class="text-xs text-n-slate-10">{{ TEXT.lastUpdate }}</div>
            <div class="mt-1 text-sm font-medium text-n-slate-12">
              {{ formatUpdatedAt(instance.updatedAt) }}
            </div>
          </div>
          <div class="flex items-center justify-start md:justify-end">
            <span
              class="inline-flex items-center gap-1 text-sm font-medium text-n-blue-10"
            >
              {{ TEXT.open }}
              <span class="i-lucide-arrow-right size-4" />
            </span>
          </div>
        </button>
      </div>
    </section>

    <section v-else class="flex-1 overflow-y-auto p-6">
      <div
        class="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
      >
        <div class="min-w-0">
          <button
            type="button"
            class="mb-2 inline-flex items-center gap-1 text-sm font-medium text-n-blue-10"
            @click="selectedInstanceId = ''"
          >
            <span class="i-lucide-chevron-left size-4" />
            {{ TEXT.back }}
          </button>
          <div class="flex flex-wrap items-center gap-2">
            <h2 class="m-0 truncate text-xl font-semibold text-n-slate-12">
              {{ selectedInstance.name }}
            </h2>
            <span
              class="rounded-md border px-2 py-1 text-xs font-medium"
              :class="statusClass(selectedInstance.status)"
            >
              {{ statusLabel(selectedInstance.status) }}
            </span>
          </div>
          <p class="m-0 mt-1 text-sm text-n-slate-10">
            {{ TEXT.channelsStyle }}
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-md bg-n-blue-9 px-3 text-sm font-medium text-white hover:bg-n-blue-10 disabled:opacity-40"
            :disabled="!connectionOk"
            @click="connectInstance(selectedInstance)"
          >
            <span class="i-lucide-qr-code size-4" />
            {{ TEXT.generateQr }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
            :disabled="!connectionOk"
            @click="refreshInstanceStatus(selectedInstance)"
          >
            <span class="i-lucide-refresh-cw size-4" />
            {{ TEXT.refreshStatus }}
          </button>
        </div>
      </div>

      <div class="mb-4 grid gap-3 md:grid-cols-4">
        <div class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="text-xs font-medium text-n-slate-10">
            {{ TEXT.connection }}
          </div>
          <div class="mt-2 text-base font-semibold text-n-slate-12">
            {{ statusLabel(selectedInstance.status) }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="text-xs font-medium text-n-slate-10">
            {{ TEXT.queuedCalls }}
          </div>
          <div class="mt-2 text-base font-semibold text-n-slate-12">
            {{ instanceQueueStats(selectedInstance).queued }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="text-xs font-medium text-n-slate-10">
            {{ TEXT.currentCall }}
          </div>
          <div class="mt-2 text-base font-semibold text-n-slate-12">
            {{ callStatusLabel(selectedInstance.call.status) }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="text-xs font-medium text-n-slate-10">
            {{ TEXT.modeSummary }}
          </div>
          <div
            class="mt-2 flex items-center gap-2 text-base font-semibold text-n-slate-12"
          >
            <span
              class="size-4"
              :class="automationModeIcon(selectedInstance.config.mode)"
            />
            {{ automationModeLabel(selectedInstance.config.mode) }}
          </div>
        </div>
      </div>

      <div class="grid gap-4 xl:grid-cols-[380px_minmax(0,1fr)]">
        <section class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <span class="i-lucide-qr-code size-5 text-n-blue-10" />
              <h3 class="m-0 text-base font-semibold text-n-slate-12">
                {{ TEXT.connection }}
              </h3>
            </div>
            <span
              class="rounded-md border px-2 py-1 text-xs font-medium"
              :class="statusClass(selectedInstance.status)"
            >
              {{ statusLabel(selectedInstance.status) }}
            </span>
          </div>
          <div
            class="flex min-h-[280px] items-center justify-center rounded-md border border-dashed border-n-weak bg-n-slate-2 p-4"
          >
            <img
              v-if="selectedInstance.qrDataUrl"
              :src="selectedInstance.qrDataUrl"
              alt="QR Code WhatsApp Web"
              class="size-60 rounded-md bg-white p-3 shadow-sm"
            />
            <div v-else class="max-w-xs text-center">
              <span
                class="i-lucide-scan-line mx-auto mb-3 block size-10 text-n-slate-8"
              />
              <div class="text-sm font-medium text-n-slate-11">
                {{ TEXT.qrEmpty }}
              </div>
              <p class="m-0 mt-1 text-sm text-n-slate-10">
                {{ TEXT.qrHint }}
              </p>
              <button
                v-if="isAdmin"
                type="button"
                class="mt-4 inline-flex items-center gap-2 rounded-md bg-n-blue-9 px-3 py-2 text-sm font-medium text-white hover:bg-n-blue-10 disabled:opacity-40"
                :disabled="!connectionOk"
                @click="connectInstance(selectedInstance)"
              >
                <span class="i-lucide-qr-code size-4" />
                {{ TEXT.generateQr }}
              </button>
            </div>
          </div>
          <div v-if="isAdmin" class="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
              :disabled="!connectionOk"
              @click="restartInstance(selectedInstance)"
            >
              <span class="i-lucide-rotate-cw size-4" />
              {{ TEXT.restart }}
            </button>
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
              :disabled="!connectionOk"
              @click="disconnectInstance(selectedInstance)"
            >
              <span class="i-lucide-unplug size-4" />
              {{ TEXT.disconnect }}
            </button>
          </div>
        </section>

        <section class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="mb-3 flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <span class="i-lucide-list-ordered size-5 text-n-blue-10" />
              <h3 class="m-0 text-base font-semibold text-n-slate-12">
                {{ TEXT.queueTitle }}
              </h3>
            </div>
            <span class="text-xs text-n-slate-10">
              {{ itemCountText(selectedInstance.queue.length) }}
            </span>
          </div>
          <textarea
            v-model="bulkNumbers"
            class="h-24 w-full resize-y rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
            :placeholder="TEXT.queueInput"
          />
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2"
              @click="addNumbersToQueue"
            >
              <span class="i-lucide-plus size-4" />
              {{ TEXT.addNumbers }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md bg-n-blue-9 px-3 py-2 text-sm font-medium text-white hover:bg-n-blue-10 disabled:opacity-40"
              :disabled="!connectionOk"
              @click="startNextCall"
            >
              <span class="i-lucide-play size-4" />
              {{ TEXT.startQueue }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
              :disabled="!hasActiveCall"
              @click="finishActiveAndNext"
            >
              <span class="i-lucide-check size-4" />
              {{ TEXT.finishNext }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
              :disabled="!hasActiveCall"
              @click="failActiveAndNext"
            >
              <span class="i-lucide-x size-4" />
              {{ TEXT.failNext }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2"
              @click="clearFinished"
            >
              <span class="i-lucide-eraser size-4" />
              {{ TEXT.clearFinished }}
            </button>
          </div>

          <div
            class="mt-4 flex flex-col gap-3 rounded-md border border-n-weak bg-n-slate-2 p-3 md:flex-row md:items-center md:justify-between"
          >
            <div class="min-w-0">
              <div class="text-xs font-medium text-n-slate-10">
                {{ TEXT.currentCall }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{ callStatusLabel(selectedInstance.call.status) }}
                <span
                  v-if="selectedInstance.call.detail"
                  class="font-normal text-n-slate-10"
                >
                  {{ selectedInstance.call.detail }}
                </span>
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-md border border-n-weak bg-n-background px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
                :disabled="!connectionOk"
                @click="refreshCallStatus"
              >
                <span class="i-lucide-activity size-4" />
                {{ TEXT.callStatus }}
              </button>
              <button
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-md border border-n-ruby-7 bg-n-background px-3 text-sm font-medium text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-40"
                :disabled="!connectionOk"
                @click="hangupActiveCall"
              >
                <span class="i-lucide-phone-off size-4" />
                {{ TEXT.hangup }}
              </button>
              <button
                v-if="!remoteRecordingActive"
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-md border border-n-weak bg-n-background px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
                :disabled="!connectionOk"
                @click="startRemoteRecording"
              >
                <span class="i-lucide-circle-dot size-4" />
                {{ TEXT.recordingStart }}
              </button>
              <button
                v-else
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-md border border-n-weak bg-n-background px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2"
                @click="stopRemoteRecording"
              >
                <span class="i-lucide-square size-4" />
                {{ TEXT.recordingStop }}
              </button>
            </div>
          </div>

          <div class="mt-4 max-h-[360px] overflow-y-auto">
            <div
              v-if="!selectedInstance.queue.length"
              class="rounded-md border border-dashed border-n-weak p-4 text-center text-sm text-n-slate-10"
            >
              {{ TEXT.emptyQueue }}
            </div>
            <div
              v-for="item in selectedInstance.queue"
              :key="item.id"
              class="mb-2 flex items-center justify-between gap-3 rounded-md border bg-n-background p-3"
              :class="queueStatusClass(item.status)"
            >
              <div class="min-w-0">
                <div class="text-sm font-semibold text-n-slate-12">
                  {{ formatPhone(item.phone) }}
                </div>
                <div class="text-xs text-n-slate-10">
                  {{ queueStatusLabel(item.status) }}
                  <span v-if="item.result">{{
                    queueResultText(item.result)
                  }}</span>
                </div>
              </div>
              <button
                type="button"
                class="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-slate-2"
                @click="removeQueueItem(item.id)"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>
          </div>
        </section>
      </div>

      <div class="mt-4 grid gap-4" :class="isAdmin ? 'xl:grid-cols-[minmax(0,1fr)_420px]' : ''">
        <section v-if="isAdmin" class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <span
                class="size-5 text-n-blue-10"
                :class="automationModeIcon(selectedInstance.config.mode)"
              />
              <h3 class="m-0 text-base font-semibold text-n-slate-12">
                {{ TEXT.automations }}
              </h3>
            </div>
            <span class="text-xs font-medium text-n-slate-10">
              {{ automationModeLabel(selectedInstance.config.mode) }}
            </span>
            <button type="button" class="inline-flex h-8 items-center gap-2 rounded-md bg-n-blue-9 px-3 text-xs font-medium text-white hover:bg-n-blue-10" @click="saveAutomationConfig(selectedInstance)">
              <span class="i-lucide-save size-3.5" /> Salvar configuração
            </button>
          </div>

          <div class="grid grid-cols-3 gap-1 rounded-md bg-n-slate-3 p-1">
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center gap-2 rounded px-2 text-sm font-medium"
              :class="
                selectedInstance.config.mode === 'manual'
                  ? 'bg-n-background text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="setAutomationMode('manual')"
            >
              <span class="i-lucide-user-round size-4" />
              {{ TEXT.manualMode }}
            </button>
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center gap-2 rounded px-2 text-sm font-medium"
              :class="
                selectedInstance.config.mode === 'audio'
                  ? 'bg-n-background text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="setAutomationMode('audio')"
            >
              <span class="i-lucide-volume-2 size-4" />
              {{ TEXT.audioMode }}
            </button>
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center gap-2 rounded px-2 text-sm font-medium"
              :class="
                selectedInstance.config.mode === 'ai'
                  ? 'bg-n-background text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="setAutomationMode('ai')"
            >
              <span class="i-lucide-bot size-4" />
              {{ TEXT.aiMode }}
            </button>
          </div>

          <div class="mt-4 grid gap-4 lg:grid-cols-2">
            <div
              v-if="selectedInstance.config.mode !== 'manual'"
              class="rounded-md border border-n-weak bg-n-slate-2 p-3"
            >
              <label class="block">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ TEXT.audioFile }}
                </span>
                <span class="mt-1 block truncate text-sm text-n-slate-12">
                  {{
                    selectedInstance.config.audioFileName || TEXT.chooseAudio
                  }}
                </span>
                <input
                  class="mt-2 block w-full text-sm text-n-slate-11"
                  type="file"
                  accept="audio/*"
                  @change="onAudioFileChange"
                />
              </label>
              <label
                class="mt-3 flex items-center gap-2 text-sm text-n-slate-11"
              >
                <input
                  v-model="selectedInstance.config.playAudioOnAnswer"
                  type="checkbox"
                />
                {{ TEXT.playOnAnswer }}
              </label>
              <button
                type="button"
                class="mt-3 inline-flex h-9 items-center gap-2 rounded-md border border-n-weak bg-n-background px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
                :disabled="
                  !connectionOk || !selectedInstance.config.audioFileId
                "
                @click="playSelectedAudioNow"
              >
                <span class="i-lucide-volume-2 size-4" />
                {{ TEXT.playAudioNow }}
              </button>
            </div>

            <div class="rounded-md border border-n-weak bg-n-slate-2 p-3">
              <div class="text-xs font-medium text-n-slate-11">
                {{ TEXT.liveMic }}
              </div>
              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  v-if="!micActive"
                  type="button"
                  class="inline-flex h-9 items-center gap-2 rounded-md border border-n-weak bg-n-background px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
                  :disabled="!connectionOk"
                  @click="startOperatorMic"
                >
                  <span class="i-lucide-mic size-4" />
                  {{ TEXT.micStart }}
                </button>
                <button
                  v-else
                  type="button"
                  class="inline-flex h-9 items-center gap-2 rounded-md border border-n-ruby-7 bg-n-background px-3 text-sm font-medium text-n-ruby-11 hover:bg-n-ruby-3"
                  @click="stopOperatorMic"
                >
                  <span class="i-lucide-mic-off size-4" />
                  {{ TEXT.micStop }}
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="selectedInstance.config.mode === 'ai'"
            class="mt-4 grid gap-3 lg:grid-cols-2"
          >
            <label class="block">
              <span class="text-xs font-medium text-n-slate-11">
                {{ TEXT.aiProvider }}
              </span>
              <select
                v-model="selectedInstance.config.ai.provider"
                class="mt-1 h-9 w-full rounded-md border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
                @change="changeAiProvider"
              >
                <option
                  v-for="provider in aiProviders"
                  :key="provider.id"
                  :value="provider.id"
                >
                  {{ provider.name }}
                </option>
              </select>
              <span v-if="selectedAiProvider" class="mt-1 block text-xs text-n-slate-10">
                {{ selectedAiProvider.description }}
              </span>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-n-slate-11">
                {{ TEXT.aiApiKey }}
              </span>
              <input
                v-model="aiApiKey"
                type="password"
                autocomplete="new-password"
                class="mt-1 h-9 w-full rounded-md border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
                :placeholder="selectedInstance.config.ai.hasApiKey ? 'Chave configurada — preencha apenas para trocar' : 'Cole a chave do provedor'"
              />
            </label>
            <label class="block">
              <span class="text-xs font-medium text-n-slate-11">
                {{ TEXT.aiModel }}
              </span>
              <select
                v-model="selectedInstance.config.ai.model"
                class="mt-1 h-9 w-full rounded-md border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8 disabled:opacity-50"
                :disabled="aiModelsLoading || !aiModels.length"
              >
                <option v-if="aiModelsLoading" value="">Atualizando modelos…</option>
                <option v-for="model in aiModels" :key="model.id" :value="model.id">
                  {{ model.name }}{{ model.contextLength ? ` · ${Number(model.contextLength).toLocaleString('pt-BR')} tokens` : '' }}
                </option>
              </select>
            </label>
            <label class="block">
              <span class="text-xs font-medium text-n-slate-11">
                {{ TEXT.aiTtsModel }}
              </span>
              <input
                v-model="selectedInstance.config.ai.ttsModel"
                class="mt-1 h-9 w-full rounded-md border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
              />
            </label>
            <div class="flex flex-wrap items-center justify-between gap-2 lg:col-span-2">
              <span v-if="aiModelsFetchedAt" class="text-xs text-n-slate-10">
                Catálogo atualizado em {{ new Date(aiModelsFetchedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}.
              </span>
              <span v-else-if="aiModelsError" class="text-xs text-n-ruby-11">{{ aiModelsError }}</span>
              <button
                type="button"
                class="inline-flex h-8 items-center gap-2 rounded-md border border-n-weak px-3 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
                :disabled="aiModelsLoading || (!aiApiKey && !selectedInstance.config.ai.hasApiKey)"
                @click="loadAiModels(selectedInstance)"
              >
                <span class="i-lucide-refresh-cw size-3.5" :class="{ 'animate-spin': aiModelsLoading }" />
                {{ aiModelsLoading ? 'Atualizando…' : 'Buscar modelos do provedor' }}
              </button>
            </div>
            <label class="block">
              <span class="text-xs font-medium text-n-slate-11">
                {{ TEXT.aiGreeting }}
              </span>
              <textarea
                v-model="selectedInstance.config.ai.greetingText"
                class="mt-1 h-16 w-full resize-y rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
              />
            </label>
            <label class="block lg:col-span-2">
              <span class="text-xs font-medium text-n-slate-11">
                {{ TEXT.aiPrompt }}
              </span>
              <textarea
                v-model="selectedInstance.config.ai.instructions"
                class="mt-1 h-20 w-full resize-y rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
              />
            </label>
            <label class="block lg:col-span-2">
              <span class="text-xs font-medium text-n-slate-11">
                {{ TEXT.aiTestText }}
              </span>
              <textarea
                v-model="aiTestText"
                class="mt-1 h-16 w-full resize-y rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
              />
            </label>
            <div class="flex flex-wrap gap-2 lg:col-span-2">
              <button
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
                :disabled="!connectionOk || aiTesting"
                @click="testAiReply({ playAudio: true })"
              >
                <span class="i-lucide-message-circle size-4" />
                {{ TEXT.aiTestReply }}
              </button>
              <button
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-md border border-n-weak px-3 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
                :disabled="!connectionOk || aiTesting"
                @click="speakAiText"
              >
                <span class="i-lucide-volume-2 size-4" />
                {{ TEXT.aiSpeak }}
              </button>
            </div>
          </div>
        </section>

        <section class="rounded-lg border border-n-weak bg-n-background p-4">
          <div class="mb-3 flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <span class="i-lucide-clock-3 size-5 text-n-blue-10" />
              <h3 class="m-0 text-base font-semibold text-n-slate-12">
                {{ TEXT.events }}
              </h3>
            </div>
          </div>
          <div
            v-if="!selectedInstance.logs.length"
            class="rounded-md border border-dashed border-n-weak p-4 text-center text-sm text-n-slate-10"
          >
            {{ TEXT.emptyEvents }}
          </div>
          <div v-else class="max-h-[520px] overflow-y-auto">
            <div
              v-for="entry in selectedInstance.logs"
              :key="entry.id"
              class="border-b border-n-weak py-2 text-sm text-n-slate-11"
            >
              <span class="mr-2 text-xs text-n-slate-9">{{ entry.at }}</span>
              {{ entry.message }}
            </div>
          </div>
        </section>
      </div>
    </section>
  </main>
</template>
